import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, Subscription, map, switchMap, take } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import {
  ButtonLayout,
  ButtonMode,
  ButtonPosition,
  ButtonTheme,
  ButtonType,
  IconPosition,
  InputType,
  NotificationTheme,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { RootPaths, unique } from '@fcom/core';
import {
  CheckInBoardingPassByEmailResponse,
  CheckInBoardingPassByEmailResponseStatus,
  FinnairBoundItem,
  FinnairOrder,
  FinnairPassengerCode,
} from '@fcom/dapi/api/models';
import { EMAIL_PATTERN, unsubscribe } from '@fcom/core/utils';
import { MmbOrderService } from '@fcom/manage-booking-common';
import { ElementActions, ElementTypes, GaContext, ToasterMessage, ToasterService } from '@fcom/common';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { GtmService } from '@fcom/common/gtm';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { CheckInService } from '@fcom/manage-booking-common/services/check-in.service';

import { FinnairPassengerItemWithApis } from '../interface/check-in.interface';

@Component({
  selector: 'fin-check-in-boarding-pass-route',
  templateUrl: 'check-in-boarding-pass-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckInBoardingPassRouteComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private formBuilder = inject(FormBuilder);
  private mmbOrderService = inject(MmbOrderService);
  private toasterService = inject(ToasterService);
  private feedbackService = inject(FeedbackService);
  private gtmService = inject(GtmService);
  private checkInService = inject(CheckInService);

  readonly ButtonPosition = ButtonPosition;
  readonly ButtonLayout = ButtonLayout;
  readonly IconPosition = IconPosition;
  readonly InputType = InputType;
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly ButtonMode = ButtonMode;
  readonly ButtonType = ButtonType;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  emailForm: FormGroup<{ email: FormControl<string> }>;
  locations$: Observable<FinnairOrder['locations']>;
  passengerIds$: Observable<string[]>;
  bound$: Observable<FinnairBoundItem>;
  order$: Observable<FinnairOrder>;
  isCarrierConnect$: Observable<boolean>;
  passengersWithCheckInInfo$: Observable<FinnairPassengerItemWithApis[]>;

  emailSendingInProgress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.feedbackService.setSurvey(QualtricsSurveys.CHECK_IN);
    this.feedbackService.setFeedbackWidgetVisibility(true);
    this.order$ = this.checkInService.getOrder();
    this.passengerIds$ = this.order$.pipe(map((o) => o.passengers.map((p) => p.id)));
    this.isCarrierConnect$ = this.checkInService.isCarrierConnect();
    this.bound$ = this.checkInService.getBound();
    this.subscriptions.add(
      this.order$.pipe(take(1)).subscribe((order) => {
        const primaryPax = order.passengers.find(({ id }) => order.primaryTravelerId === id);

        this.emailForm = this.formBuilder.group({
          email: new FormControl(primaryPax?.email ?? '', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
        });
      })
    );

    this.locations$ = this.order$.pipe(map(({ locations }) => locations));

    this.passengersWithCheckInInfo$ = combineLatest([this.order$, this.bound$, this.passengerIds$]).pipe(
      map(([order, bound, passengerIds]) => {
        const checkInEligibilityForBound = order?.eligibilities?.checkIn?.find(
          (eligibility) => eligibility.id === bound.id
        );

        const checkinFlightEligibilities = checkInEligibilityForBound?.flightTravelerEligibility ?? {};

        return order.passengers
          .filter(({ id }) => passengerIds.includes(id))
          .map((passenger) => {
            const eligibilities = Object.entries(checkinFlightEligibilities)
              .flatMap(([flightId, flightCheckinEligibility]) => {
                const eligibility = flightCheckinEligibility.find((eligibility) =>
                  eligibility.travelerIds.includes(passenger.id)
                );

                return eligibility
                  ? {
                      ...eligibility,
                      flightId,
                    }
                  : undefined;
              })
              .filter(Boolean);

            return {
              ...passenger,
              eligibilities,
            };
          });
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  navigateToMmb(): void {
    this.trackElement('navigate-to-mmb');
    this.router.navigateByUrl(
      `${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
    );
  }

  sendEmail(): void {
    this.trackElement('send-email', ElementActions.SUBMIT);
    this.emailSendingInProgress$.next(true);

    this.subscriptions.add(
      combineLatest([this.order$, this.passengersWithCheckInInfo$])
        .pipe(
          take(1),
          switchMap(([order, passengers]) => {
            const flightIds = passengers.reduce((all, passenger) => {
              const passengerFlightIds = passenger.eligibilities
                .filter((e) => e.isAllowedToUseBoardingPass)
                .map((e) => e.flightId);

              return [...new Set([...all, ...passengerFlightIds])];
            }, [] as string[]);

            const travelerIds = passengers.reduce((all, passenger) => {
              const eligibleTravelerIds = passenger.eligibilities
                .filter((e) => e.isAllowedToUseBoardingPass)
                .flatMap((e) => e.travelerIds)
                .filter(unique);

              return [...new Set([...all, ...eligibleTravelerIds])];
            }, [] as string[]);

            return this.mmbOrderService.sendBoardingPass(order.id, order.hash, {
              flightIds,
              emailAddress: this.emailForm.controls.email.value,
              travelerIds,
            });
          })
        )
        .subscribe({
          next: (response: CheckInBoardingPassByEmailResponse) => {
            const results = Object.values(response?.results ?? {}).flatMap((flightResults) =>
              Object.values(flightResults)
            );
            const allSuccess =
              results.length > 0 &&
              results.every((result) => result === CheckInBoardingPassByEmailResponseStatus.SUCCESS);
            const partialSuccess = results.some(
              (result) => result === CheckInBoardingPassByEmailResponseStatus.SUCCESS
            );
            const message = allSuccess
              ? this.getToasterMessage('success')
              : partialSuccess
                ? this.getToasterMessage('partialSuccess')
                : this.getToasterMessage('error', NotificationTheme.ALERT);
            this.toasterService.addMessageToQueue(message);
            this.emailSendingInProgress$.next(false);
          },
          error: () => {
            this.toasterService.addMessageToQueue(this.getToasterMessage('error', NotificationTheme.ALERT));
            this.emailSendingInProgress$.next(false);
          },
        })
    );
  }

  private trackElement(elementNameSuffix: string, action = ElementActions.CLICK): void {
    this.gtmService.trackElement(
      `boarding-pass_${elementNameSuffix}`,
      GaContext.CHECK_IN,
      ElementTypes.BUTTON,
      undefined,
      action
    );
  }

  private getToasterMessage(key: string, theme: ToasterMessage['theme'] = NotificationTheme.SUCCESS): ToasterMessage {
    return {
      id: 'boardingPass',
      theme,
      contentKey: `MMB.checkIn.confirmation.notifications.boardingPass.${key}`,
    };
  }
}
